import React, { useState, useEffect, useRef } from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import ButtonFilledCancel from "../requests/shared/helpers/ButtonFilledCancel";
import CircularProgress from "@mui/material/CircularProgress";
import { Lightbox } from "react-modal-image";
import { toast } from "react-toastify";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider } from "@mui/material/styles";
import violetTheme from "../../../theme/rightworkTheme";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonFilled from "../requests/shared/helpers/ButtonFilled";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import {
  uploadFile,
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { DatePicker } from "@mui/x-date-pickers";
import {
  ISSUE_TYPE_OPTIONS,
  CATEGORY_ISSUE_TYPE_MAP,
  CATEGORY_CONFIG,
  PRIORITY_OPTIONS,
  RECURRENCE_INTERVAL_OPTIONS,
  MENU_OPTIONS,
  getDefaultStatusOptions,
  openStatusOptions,
  resolveStatusLabel,
  withLegacyMenus,
} from "./MaintenanceBadges";
import {
  API_get_maintenance_comments,
  API_post_maintenance_comment,
  API_get_maintenance_assets,
  API_get_maintenance_requests,
} from "../../../constants";
import AssetPicker from "./assets/AssetPicker";
import CostAndPartsSection, { partsForPayload } from "./assets/CostAndPartsSection";
import ButtonGrayed from "../requests/shared/helpers/ButtonGrayed";
import RightworkDatePicker from "../../../components/shared/RightworkDatePicker";



const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "webm", "m4v", "avi", "hevc"]);
// HEIC/HEIF are accepted for upload but the backend re-encodes them to JPEG
// (browsers other than Safari can't render them); until that runs they show
// as a generic file rather than a broken image.
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);

function getFileExtension(file) {
  if (file.extension) return file.extension.toLowerCase();
  const source = file.name || file.presigned_url || "";
  return source.split('.').pop()?.split('?')[0]?.toLowerCase() || "";
}

function getFileKind(file) {
  const ext = getFileExtension(file);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
}

const selectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "45px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": {
      borderColor: "#6d28d9",
    },
  },
};

// The asset's data plate fields, in the ticket's equipment_details shape.
function equipmentFromAsset(asset) {
  if (!asset) return {};
  const out = {};
  ["manufacturer", "model_number", "serial_number"].forEach((key) => {
    if (asset[key]) out[key] = asset[key];
  });
  return out;
}

function MaintenanceTicketDrawer(props) {
  const {
    isOpen,
    initialTab: initialTabProp,
    onClose,
    ticket,
    onSubmit,
    onDelete,
    onDecideApproval,
    onCommentPosted,
    permissionRole,
    permissionOverrides,
    homeCompany,
    homeStore,
    storeNames,
    // Stores offered when creating: the category must be on there and the
    // user must be able to raise its tickets. Editing still lists storeNames.
    createStoreNames,
    employees,
    approvers,
    onStoreChange,
    category: categoryProp,
    issueTypeOptions: issueTypeOptionsProp,
    priorityOptions: priorityOptionsProp,
    statusOptions: statusOptionsProp,
    menuOptions: menuOptionsProp,
    optionsStatus = "ready", // loading | ready | error — from the parent's options fetch
    onRetryOptions,
    // Seed for a NEW ticket (title, description, issue_type, priority,
    // location_detail), e.g. a ticket raised from a failed audit item. Read
    // through a ref so a parent passing a fresh object each render never
    // resets the form mid-edit.
    initialValues,
    // Asset to start a NEW ticket on (from an asset's page). Same ref
    // treatment as initialValues.
    initialAsset,
  } = props;
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const initialAssetRef = useRef(initialAsset);
  initialAssetRef.current = initialAsset;

  const isEdit = !!ticket;
  const isReadOnly = ["completed", "closed"].includes(ticket?.status);
  const isManager = ["company_manager", "company_admin", "rightwork_internal"].includes(permissionRole);
  const hasAnyTicketAccess = permissionOverrides?.some((o) =>
    ["maintenance_access", "it_access", "marketing_access", "menu_access"].includes(o)
  );
  const canManageMaintenance = isManager || hasAnyTicketAccess;

  // Use ticket's category for edit mode, prop category for create mode
  const effectiveCategory = isEdit ? (ticket?.category || "maintenance") : (categoryProp || "maintenance");
  const issueTypeOptions = issueTypeOptionsProp?.length ? issueTypeOptionsProp : (CATEGORY_ISSUE_TYPE_MAP[effectiveCategory] || ISSUE_TYPE_OPTIONS);
  const priorityOptions = priorityOptionsProp?.length ? priorityOptionsProp : PRIORITY_OPTIONS;
  // The category's status wording (menu relabels and hides Waiting: Vendor).
  const statusOptions = statusOptionsProp?.length ? statusOptionsProp : getDefaultStatusOptions(effectiveCategory);
  // Open statuses for the form. A ticket already on a status the category
  // hides (e.g. an old Waiting: Vendor) keeps it listed so it isn't blanked.
  const formStatusOptions = (() => {
    const open = openStatusOptions(statusOptions);
    const current = ticket?.status;
    if (current && !["completed", "closed"].includes(current) && !open.some((o) => o.value === current)) {
      return [...open, { value: current, label: resolveStatusLabel(current) }];
    }
    return open;
  })();
  const menuOptions = menuOptionsProp?.length ? menuOptionsProp : MENU_OPTIONS;
  const optionsReady = optionsStatus === "ready";
  const EMPTY_EQUIPMENT = { manufacturer: "", model_number: "", serial_number: "", plate_notes: "" };
  const EQUIPMENT_FIELDS = [
    { key: "manufacturer", label: "Manufacturer" },
    { key: "model_number", label: "Model number" },
    { key: "serial_number", label: "Serial number" },
  ];

  const isMenu = effectiveCategory === "menu";
  const EMPTY_MENU = { go_live_date: "", menus: [], online_ordering: null, replaces_item: "", kill_date: "" };

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    issue_type: "",
    priority: "normal",
    location_detail: "",
    equipment_details: { manufacturer: "", model_number: "", serial_number: "", plate_notes: "" },
    menu_details: { go_live_date: "", menus: [], online_ordering: null, replaces_item: "", kill_date: "" },
    status: "new",
    vendor: "",
    scheduled_time: "",
  });
  // Asset link (maintenance tickets at a single store only) and the
  // optional cost/parts recorded when closing out the work.
  const [asset, setAsset] = useState(null);
  const [storeAssets, setStoreAssets] = useState([]);
  const [openAssetTickets, setOpenAssetTickets] = useState([]);
  const [repairCost, setRepairCost] = useState("");
  const [partsUsed, setPartsUsed] = useState([]);
  const hasEquipmentValues = Object.values(formData.equipment_details || {}).some((v) => v && String(v).trim());
  // Shown for issue types the company flagged (e.g. Equipment, Refrigeration),
  // whenever a ticket already carries details so a type change never hides
  // them, and when an asset is linked (its data plate fills the block).
  const showEquipmentBlock =
    Boolean(issueTypeOptions.find((o) => o.value === formData.issue_type)?.equipment_details) ||
    hasEquipmentValues ||
    Boolean(asset);
  const setEquipmentField = (key, value) =>
    setFormData((prev) => ({
      ...prev,
      equipment_details: { ...(prev.equipment_details || {}), [key]: value },
    }));
  const setMenuField = (key, value) =>
    setFormData((prev) => ({
      ...prev,
      menu_details: { ...(prev.menu_details || {}), [key]: value },
    }));
  // Editing a menu ticket only offers stores with menu tickets on (plus its
  // current store); the backend rejects a move anywhere else.
  const pickerStores = isEdit
    ? (effectiveCategory === "menu"
        ? (storeNames || []).filter((s) => s.menu_tickets_enabled || s.id === ticket?.store?.id)
        : storeNames)
    : (createStoreNames || storeNames);
  const [assignedToIds, setAssignedToIds] = useState([]);
  const [approverIds, setApproverIds] = useState([]);
  const [decidingAction, setDecidingAction] = useState(null); // "approve" | "reject" | null
  const [rejectNote, setRejectNote] = useState("");
  const [isDeciding, setIsDeciding] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [fileUploads, setFileUploads] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState("weekly");
  const [recurrenceCount, setRecurrenceCount] = useState(1);
  const [recurrenceTrigger, setRecurrenceTrigger] = useState("on_completion");
  const [startDate, setStartDate] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Conversation tab state
  const [activeTab, setActiveTab] = useState("details");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const commentsEndRef = useRef(null);
  const activeFetchUuidRef = useRef(null);
  const commentSeqRef = useRef(0);

  const fetchComments = async (uuid, { clear = true, showLoading = true } = {}) => {
    activeFetchUuidRef.current = uuid;
    const seq = ++commentSeqRef.current;
    if (clear) setComments([]);
    if (showLoading) setCommentsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_maintenance_comments, {
        store_id: homeStore,
        maintenance_request_uuid: uuid,
      });
      if (activeFetchUuidRef.current !== uuid || commentSeqRef.current !== seq) return;
      setComments(res.data?.redux?.payload?.maintenance_comments || []);
    } catch (error) {
      // Comments already cleared above
    } finally {
      if (activeFetchUuidRef.current === uuid) {
        setCommentsLoading(false);
      }
    }
  };

  const handlePostComment = async () => {
    if (!commentText.trim() || !ticket || postingComment) return;
    const submittedUuid = ticket.uuid;
    setPostingComment(true);
    try {
      const res = await postRightworkAPI(API_post_maintenance_comment, {
        store_id: homeStore,
        maintenance_request_uuid: submittedUuid,
        text: commentText.trim(),
      });
      if (activeFetchUuidRef.current !== submittedUuid) return; // Ticket changed
      const newComment = res.data?.redux?.payload?.maintenance_comment;
      if (newComment) {
        setComments((prev) => [...prev, newComment]);
      }
      setCommentText("");
      // Refetch full thread to ensure completeness (e.g. if initial fetch was discarded)
      fetchComments(submittedUuid, { clear: false, showLoading: false });
      // Notify parent to refresh ticket list (updates comment_count)
      onCommentPosted?.();
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      if (activeFetchUuidRef.current === submittedUuid) {
        toast.error("Failed to post comment");
      }
    } finally {
      if (activeFetchUuidRef.current === submittedUuid) {
        setPostingComment(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTabProp || "details");
      setCommentText("");
      setComments([]);
      setCommentsLoading(false);
      setPostingComment(false);
      setDecidingAction(null);
      setRejectNote("");
      setIsDeciding(false);
      activeFetchUuidRef.current = null;
      if (ticket) {
        setFormData({
          title: ticket.title || "",
          description: ticket.description || "",
          issue_type: ticket.issue_type || "",
          priority: ticket.priority || "normal",
          location_detail: ticket.location_detail || "",
          equipment_details: { ...EMPTY_EQUIPMENT, ...(ticket.equipment_details || {}) },
          menu_details: { ...EMPTY_MENU, ...withLegacyMenus(ticket.menu_details) },
          status: ticket.status || "new",
          vendor: ticket.vendor || "",
          scheduled_time: ticket.scheduled_time ? new Date(ticket.scheduled_time) : null,
        });
        setAssignedToIds((ticket.assignees || []).map((a) => a.id));
        setApproverIds((ticket.approvers || []).map((a) => a.id));
        setSelectedStoreIds([ticket.store?.id || homeStore]);
        setFileUploads(ticket.file_uploads || []);
        setIsRecurring(false);
        setAsset(ticket.asset || null);
        setRepairCost(ticket.repair_cost || "");
        setPartsUsed(ticket.parts_used || []);
        setOpenAssetTickets([]);
      } else {
        const seed = initialValuesRef.current || {};
        const seedAsset = initialAssetRef.current;
        setAsset(seedAsset || null);
        setRepairCost("");
        setPartsUsed([]);
        setOpenAssetTickets([]);
        setFormData({
          title: seed.title || "",
          description: seed.description || "",
          issue_type: seed.issue_type || "",
          priority: seed.priority || "normal",
          location_detail: seed.location_detail || "",
          equipment_details: { ...EMPTY_EQUIPMENT, ...equipmentFromAsset(seedAsset) },
          menu_details: { ...EMPTY_MENU },
          status: "new",
          vendor: "",
          scheduled_time: null,
        });

        setAssignedToIds([]);
        setApproverIds([]);
        // Start on the home store when it can take this category's tickets;
        // otherwise the only eligible store, or nothing so the user picks.
        const createStores = createStoreNames || storeNames || [];
        let defaultStoreIds = [homeStore];
        if (seedAsset?.store?.id) {
          // A ticket started from an asset belongs at the asset's store.
          defaultStoreIds = [seedAsset.store.id];
        } else if (createStoreNames && !createStores.some((s) => s.id === homeStore)) {
          defaultStoreIds = createStores.length === 1 ? [createStores[0].id] : [];
        }
        setSelectedStoreIds(defaultStoreIds);
        if (defaultStoreIds.length === 1 && defaultStoreIds[0] !== homeStore) {
          onStoreChange?.(defaultStoreIds[0]);
        }
        setFileUploads([]);
        setIsRecurring(false);
        setRecurrenceInterval("weekly");
        setRecurrenceCount(1);
        setRecurrenceTrigger("on_completion");
        setStartDate(null);
      }
    }
  }, [isOpen, ticket, homeStore]);

  useEffect(() => {
    if (activeTab === "conversation" && ticket?.uuid) {
      fetchComments(ticket.uuid);
    }
  }, [activeTab, ticket?.uuid]);

  // Assets at the ticket's store, for the picker. Only maintenance tickets
  // at a single store can link one, and recurring templates never do.
  const assetStoreId = selectedStoreIds.length === 1 ? selectedStoreIds[0] : null;
  const canLinkAsset = effectiveCategory === "maintenance" && !isRecurring && !!assetStoreId;
  useEffect(() => {
    // Drop the previous store's list first so it can't be picked while the
    // new one loads (or if it fails).
    setStoreAssets([]);
    if (!isOpen || !canLinkAsset) return;
    let cancelled = false;
    getRightworkAPI(API_get_maintenance_assets, { store_id: homeStore, asset_store_id: assetStoreId })
      .then((res) => {
        if (!cancelled) setStoreAssets(res.data?.redux?.payload?.maintenance_assets || []);
      })
      .catch(() => {
        // No asset access at that store: the picker just stays hidden.
        if (!cancelled) setStoreAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, canLinkAsset, homeStore, assetStoreId]);

  // On a new ticket, point out work already open on the picked asset so the
  // same breakdown isn't reported twice.
  useEffect(() => {
    // Drop the previous asset's warning first; it says nothing about this one.
    setOpenAssetTickets([]);
    if (!isOpen || isEdit || !asset?.uuid) return;
    let cancelled = false;
    getRightworkAPI(API_get_maintenance_requests, {
      store_id: homeStore,
      category: "maintenance",
      asset_uuid: asset.uuid,
    })
      .then((res) => {
        if (cancelled) return;
        const tickets = res.data?.redux?.payload?.maintenance_requests || [];
        setOpenAssetTickets(tickets.filter((t) => !["completed", "closed"].includes(t.status)));
      })
      .catch(() => {
        if (!cancelled) setOpenAssetTickets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, asset?.uuid, homeStore]);

  const handleAssetChange = (next) => {
    // The previous asset's data plate: the store list's copy when listed,
    // else the ticket's own link (which carries it even once archived).
    const previous = asset ? storeAssets.find((a) => a.uuid === asset.uuid) || asset : null;
    const fromPrevious = equipmentFromAsset(previous);
    const fromNext = equipmentFromAsset(next);
    setAsset(next);
    // Swap the data plate fields over: blank ones and ones still holding the
    // previous asset's value take the new asset's; anything typed stays.
    setFormData((prev) => {
      const current = prev.equipment_details || {};
      const merged = { ...current };
      ["manufacturer", "model_number", "serial_number"].forEach((key) => {
        const value = String(current[key] || "").trim();
        if (!value || (fromPrevious[key] && value === fromPrevious[key])) {
          merged[key] = fromNext[key] || "";
        }
      });
      return { ...prev, equipment_details: merged };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsFileLoading(true);
    try {
      const result = await uploadFile(homeCompany, file, "maintenance_request");
      if (result?.uuid) {
        setFileUploads((prev) => [...prev, {
          uuid: result.uuid,
          presigned_url: result.presigned_url || null,
          extension: result.extension || null,
          name: result.name || null,
        }]);
      }
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setIsFileLoading(false);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (uuidToRemove) => {
    setFileUploads((prev) => prev.filter((f) => f.uuid !== uuidToRemove));
  };

  const handleSubmit = async (statusOverride) => {
    if (!optionsReady) {
      toast.error("Ticket options are still loading. Please wait or retry.");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.issue_type) {
      toast.error("Issue type is required");
      return;
    }

    if (!isEdit && selectedStoreIds.length === 0) {
      toast.error("Please select at least one store");
      return;
    }

    if (isRecurring && !startDate) {
      toast.error("Start date is required for recurring tickets");
      return;
    }

    if (
      !isRecurring &&
      (statusOverride || formData.status) === "waiting_approval" &&
      approverIds.length === 0
    ) {
      toast.error("An approver is required for a Waiting: Approval ticket");
      return;
    }

    setIsSubmitting(true);
    setSubmittingAction(statusOverride || "update");
    try {
      if (isRecurring && !isEdit) {
        // Create recurring template instead of a regular ticket
        const payload = {
          category: effectiveCategory,
          title: formData.title,
          description: formData.description,
          issue_type: formData.issue_type,
          priority: formData.priority,
          // Recurring templates carry a single assignee — send the first
          // selected (the picker is multi-select for one-off tickets).
          assigned_to_store_team_member_id: assignedToIds[0] || null,
          recurrence_interval: recurrenceInterval,
          recurrence_count: recurrenceCount,
          recurrence_trigger: recurrenceTrigger,
          start_date: startDate
            ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`
            : null,
          store_ids: selectedStoreIds,
        };
        await onSubmit(payload);
      } else {
        const effectiveStatus = statusOverride || formData.status;
        // Menu tickets schedule a go-live, never a vendor visit.
        const keepsScheduleDetails =
          !isMenu && ["scheduled", "completed", "closed"].includes(effectiveStatus);
        const uuids = fileUploads.map((f) => f.uuid);
        const payload = {
          ...formData,
          category: effectiveCategory,
          status: effectiveStatus,
          vendor: keepsScheduleDetails ? formData.vendor : "",
          scheduled_time: keepsScheduleDetails && formData.scheduled_time
            ? formData.scheduled_time.toISOString()
            : null,
          file_upload_uuids: JSON.stringify(uuids),
          equipment_details: JSON.stringify(formData.equipment_details || {}),
          menu_details: JSON.stringify(formData.menu_details || {}),
          ...(isEdit ? { maintenance_request_uuid: ticket.uuid } : {}),
          assigned_to_store_team_member_ids: JSON.stringify(assignedToIds),
          // Only send the approvers while the ticket is in Waiting: Approval —
          // omitting the key otherwise leaves a prior approver/decision intact.
          ...(effectiveStatus === "waiting_approval"
            ? { approver_store_team_member_ids: JSON.stringify(approverIds) }
            : {}),
          store_ids: selectedStoreIds,
        };
        // Asset link: maintenance tickets only. An edit always sends it (""
        // clears); a create only when one is picked.
        if (effectiveCategory === "maintenance") {
          if (isEdit) payload.asset_uuid = asset?.uuid || "";
          else if (asset?.uuid && canLinkAsset) payload.asset_uuid = asset.uuid;
        }
        // Cost and parts are recorded on existing maintenance tickets.
        if (isEdit && effectiveCategory === "maintenance") {
          payload.repair_cost = repairCost;
          payload.parts_used = JSON.stringify(partsForPayload(partsUsed));
        }
        // The menu block only belongs to menu tickets.
        if (!isMenu) delete payload.menu_details;
        // Menu tickets never edit vendor or scheduled time: leave whatever
        // is stored untouched instead of clearing it.
        if (isMenu) {
          delete payload.vendor;
          delete payload.scheduled_time;
        }
        await onSubmit(payload);
      }
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Failed to save ticket"
      );
    } finally {
      setIsSubmitting(false);
      setSubmittingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to archive this ticket?")) return;
    try {
      await onDelete(ticket.uuid);
      onClose();
    } catch (error) {
      toast.error("Failed to archive ticket");
    }
  };

  // --- Approval ---
  // A ticket's current approvers may have been removed from the company's
  // approver pool since assignment; keep them selectable in the dropdown.
  const approverOptions = (() => {
    const opts = [...(approvers || [])];
    (ticket?.approvers || []).forEach((a) => {
      if (a?.id && !opts.some((o) => o.id === a.id)) {
        opts.push({ id: a.id, full_name: a.full_name });
      }
    });
    return opts;
  })();

  // Likewise keep the ticket's current assignees selectable even if they've
  // since been excluded from the category's assignable pool.
  const assigneeOptions = (() => {
    const opts = [...(employees || [])];
    (ticket?.assignees || []).forEach((a) => {
      if (a?.id && !opts.some((o) => o.id === a.id)) {
        opts.push({ id: a.id, full_name: a.full_name });
      }
    });
    return opts;
  })();

  const approverPoolEmpty = (approvers || []).length === 0;
  const approvalPending = isEdit && ticket?.approval_status === "pending";
  const approvalRejected = isEdit && ticket?.approval_status === "rejected";

  // Approvers are scoped to a single store, so "Waiting: Approval" can't be
  // chosen for a multi-store create. This holds the reason it's unavailable
  // as a new selection (null = available).
  const isMultiStoreCreate = !isEdit && selectedStoreIds.length > 1;
  const waitingApprovalDisabledReason = isMultiStoreCreate
    ? "Create the ticket for a single store to use Waiting: Approval."
    : approverPoolEmpty
    ? "“Waiting: Approval” needs at least one approver. Admins can add them in ticket assignment settings."
    : null;

  const handleApprove = async () => {
    setIsDeciding(true);
    try {
      await onDecideApproval(ticket.uuid, "approve", "");
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Failed to approve ticket"
      );
    } finally {
      setIsDeciding(false);
    }
  };

  const handleReject = async () => {
    if (!rejectNote.trim()) {
      toast.error("Please add a note explaining the rejection");
      return;
    }
    setIsDeciding(true);
    try {
      await onDecideApproval(ticket.uuid, "reject", rejectNote.trim());
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Failed to reject ticket"
      );
    } finally {
      setIsDeciding(false);
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      {showLightbox && selectedImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      {showVideoModal && selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-10 right-0 text-white hover:text-slate-300"
              onClick={() => setShowVideoModal(false)}
            >
              <CloseIcon />
            </button>
            <video
              controls
              autoPlay
              src={selectedVideo}
              className="w-full rounded-lg"
              style={{ maxHeight: "80vh" }}
            />
          </div>
        </div>
      )}
      <div className="w-[450px] h-full bg-slate-50 relative flex flex-col">
        <div className="flex-1 overflow-auto">
          <DrawerHeader
            title={
              isEdit
                ? `${CATEGORY_CONFIG[effectiveCategory]?.label || "Maintenance"} Ticket${
                    ticket?.ticket_number != null ? ` #${ticket.ticket_number}` : ""
                  }`
                : `New ${CATEGORY_CONFIG[effectiveCategory]?.label || "Maintenance"} Ticket`
            }
            onClose={onClose}
          />

          {/* Tabs (edit mode only) */}
          {isEdit && (
            <div className="flex border-b border-slate-200 px-6">
              <button
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "details"
                    ? "border-violet-700 text-violet-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "conversation"
                    ? "border-violet-700 text-violet-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                onClick={() => setActiveTab("conversation")}
              >
                Conversation
                {ticket?.comment_count > 0 && (
                  <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${
                    activeTab === "conversation"
                      ? "bg-violet-100 text-violet-700"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                    {ticket.comment_count}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Conversation Tab */}
          {activeTab === "conversation" && isEdit && (<>
            <div className="px-6 py-4">
              {commentsLoading ? (
                <div className="flex items-center justify-center py-10">
                  <CircularProgress size={24} sx={{ color: "#6d28d9" }} />
                </div>
              ) : comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <p className="text-sm">No comments yet</p>
                  <p className="text-xs mt-1">Start the conversation below</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.uuid} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {comment.author?.initials || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900">
                            {comment.author?.full_name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {comment.created_at_formatted}
                          </p>
                        </div>
                        <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </div>
              )}
            </div>
            {/* Spacer for pinned input */}
            <div className="h-[100px]"></div>
          </>)}


          {/* Details Tab */}
          {(activeTab === "details" || !isEdit) && (<>
          <div className="w-full px-6">
            {/* Approval action panel — shown to the designated approver */}
            {isEdit && ticket?.can_approve && (
              <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50 p-4">
                <p className="text-sm font-semibold text-violet-900">
                  Awaiting your approval
                </p>
                <p className="text-xs text-violet-700 mt-0.5">
                  Approve to unlock completion, or reject to send it back to In Progress.
                </p>
                {decidingAction === "reject" ? (
                  <div className="mt-3">
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="Reason for rejection (required)..."
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      sx={textFieldSx}
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={isDeciding}
                        className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60"
                      >
                        {isDeciding ? "Rejecting..." : "Confirm Reject"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDecidingAction(null); setRejectNote(""); }}
                        disabled={isDeciding}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={isDeciding}
                      className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60"
                    >
                      {isDeciding ? "Approving..." : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecidingAction("reject")}
                      disabled={isDeciding}
                      className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Approval status — read-only summary */}
            {isEdit && !ticket?.can_approve && ticket?.approval_status && (
              <div
                className={`mt-3 rounded-xl border p-3 ${
                  ticket.approval_status === "approved"
                    ? "border-emerald-200 bg-emerald-50"
                    : ticket.approval_status === "rejected"
                    ? "border-red-200 bg-red-50"
                    : "border-violet-200 bg-violet-50"
                }`}
              >
                {ticket.approval_status === "pending" && (
                  <div className="flex items-center gap-1.5 text-sm text-violet-900">
                    <HourglassEmptyIcon sx={{ fontSize: 18 }} />
                    <span>
                      Awaiting approval
                      {ticket.approvers?.length
                        ? ` from ${ticket.approvers.map((a) => a.full_name).join(", ")}`
                        : ""}
                    </span>
                  </div>
                )}
                {ticket.approval_status === "approved" && (
                  <div className="flex items-center gap-1.5 text-sm text-emerald-900">
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                    <span>
                      Approved
                      {ticket.approval_decided_by
                        ? ` by ${ticket.approval_decided_by.full_name}`
                        : ""}
                    </span>
                  </div>
                )}
                {ticket.approval_status === "rejected" && (
                  <div className="flex items-center gap-1.5 text-sm text-red-900">
                    <CancelOutlinedIcon sx={{ fontSize: 18 }} />
                    <span>
                      Rejected
                      {ticket.approval_decided_by
                        ? ` by ${ticket.approval_decided_by.full_name}`
                        : ""}
                    </span>
                  </div>
                )}
                {ticket.approval_note && (
                  <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">
                    “{ticket.approval_note}”
                  </p>
                )}
              </div>
            )}

            {/* Title */}
            <div className="mt-2">
              <p className="text-sm text-slate-500">Title *</p>
              <div className="mt-1">
                <TextField
                  placeholder={isMenu ? "e.g. Add Spring Burger to dinner menu" : "Brief summary of the issue..."}
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  sx={textFieldSx}
                />
              </div>
            </div>

            {/* Description */}
            <div className="mt-5">
              <p className="text-sm text-slate-500">
                {isMenu ? "Details of what is changing" : "Description"}
              </p>
              {isMenu && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Include name, ingredients, price, dietary, etc. where necessary.
                </p>
              )}
              <div className="mt-1">
                <TextField
                  placeholder={
                    isMenu
                      ? "e.g. Spring Burger: brioche bun, aged cheddar, pickled onion. $18. Contains dairy and gluten."
                      : "Describe the issue in detail..."
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  multiline
                  rows={4}
                  sx={textFieldSx}
                />
              </div>
            </div>

            {/* Make Recurring toggle (create mode only) */}
            {!isEdit && (
              <div className="mt-5">
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Make this recurring</p>
                    <p className="text-xs text-slate-400 mt-0.5">Auto-create tickets on a schedule</p>
                  </div>
                  <Switch
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#6d28d9" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6d28d9" },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Recurring fields */}
            {isRecurring && !isEdit && (
              <>
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Recurrence *</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-slate-600">Every</span>
                    <TextField
                      type="number"
                      value={recurrenceCount}
                      onChange={(e) => setRecurrenceCount(e.target.value === "" ? "" : parseInt(e.target.value) || "")}
                      onBlur={() => setRecurrenceCount((prev) => Math.max(1, parseInt(prev) || 1))}
                      size="small"
                      inputProps={{ min: 1, style: { textAlign: "center" } }}
                      sx={{ ...textFieldSx, width: 70, "& .MuiOutlinedInput-root": { ...textFieldSx["& .MuiOutlinedInput-root"], height: "45px" } }}
                    />
                    <Select
                      value={recurrenceInterval}
                      onChange={(e) => setRecurrenceInterval(e.target.value)}
                      size="small"
                      sx={{ ...selectSx, minWidth: 110 }}
                    >
                      {RECURRENCE_INTERVAL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-sm text-slate-500">Start Date *</p>
                  <div className="mt-1">
                    <ThemeProvider theme={violetTheme}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          minDate={new Date()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              placeholder: "Select start date...",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  height: "45px",
                                  backgroundColor: "white",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#6d28d9",
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-sm text-slate-500">Trigger Mode</p>
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        recurrenceTrigger === "on_completion"
                          ? "border-violet-600 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setRecurrenceTrigger("on_completion")}
                    >
                      After completion
                    </button>
                    <button
                      type="button"
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        recurrenceTrigger === "fixed"
                          ? "border-violet-600 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setRecurrenceTrigger("fixed")}
                    >
                      Fixed schedule
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {(() => {
                      const count = parseInt(recurrenceCount) || 1;
                      const units = { daily: ["day", "days"], weekly: ["week", "weeks"], monthly: ["month", "months"] };
                      const unit = (units[recurrenceInterval] || units.daily)[count === 1 ? 0 : 1];
                      const cadence = count === 1 ? `1 ${unit}` : `${count} ${unit}`;
                      return recurrenceTrigger === "on_completion"
                        ? `Next ticket created ${cadence} after the current one is completed`
                        : `Creates a ticket every ${cadence} regardless of completion`;
                    })()}
                  </p>
                </div>
              </>
            )}

            {/* Status (hidden when recurring or completed) */}
            {!isRecurring && !["completed", "closed"].includes(formData.status) && (
            <div className="mt-5">
              <p className="text-sm text-slate-500">Status</p>
              <div className="mt-1">
                <Select
                  fullWidth
                  value={formData.status}
                  name="status"
                  onChange={handleChange}
                  disabled={isReadOnly}
                  size="small"
                  sx={selectSx}
                >
                  {formStatusOptions.map((opt) => {
                    // "Waiting: Approval" is disabled when it can't be used
                    // (empty approver pool, or multi-store create) — unless
                    // the ticket is already on that status (keep it selectable).
                    const optionDisabled =
                      opt.value === "waiting_approval" &&
                      !!waitingApprovalDisabledReason &&
                      opt.value !== ticket?.status;
                    return (
                      <MenuItem key={opt.value} value={opt.value} disabled={optionDisabled}>
                        {opt.label}
                      </MenuItem>
                    );
                  })}
                </Select>
                {waitingApprovalDisabledReason && formData.status !== "waiting_approval" && (
                  <p className="text-xs text-slate-400 mt-1">
                    {waitingApprovalDisabledReason}
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Approver(s) (shown directly under Status when Waiting: Approval).
                Any one approver can decide the ticket. */}
            {canManageMaintenance && formData.status === "waiting_approval" && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">Approver(s) *</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Any one of them can approve or reject.
                </p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    multiple
                    value={approverIds}
                    onChange={(e) => setApproverIds(e.target.value)}
                    disabled={isReadOnly}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected.length) {
                        return <span className="text-slate-400">Select approver(s)...</span>;
                      }
                      const names = approverOptions
                        .filter((a) => selected.includes(a.id))
                        .map((a) => a.full_name);
                      if (names.length <= 2) return names.join(", ");
                      return `${names[0]}, +${names.length - 1} more`;
                    }}
                    sx={selectSx}
                  >
                    {approverOptions.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        <Checkbox
                          checked={approverIds.includes(a.id)}
                          sx={{ "&.Mui-checked": { color: "#6d28d9" } }}
                        />
                        <ListItemText primary={a.full_name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {approverOptions.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No approvers configured for this category. Admins can add
                      them in ticket assignment settings.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Vendor & Scheduled Time (hidden when recurring, and on menu
                tickets, which schedule a go-live rather than a vendor visit) */}
            {!isRecurring && !isMenu && (formData.status === "scheduled" || (["completed", "closed"].includes(formData.status) && (formData.vendor || formData.scheduled_time))) && (
              <>
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Vendor</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="e.g. ABC Plumbing Co."
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="vendor"
                      value={formData.vendor}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      sx={textFieldSx}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Scheduled Time</p>
                  <div className="mt-1">
                    <ThemeProvider theme={violetTheme}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          views={["month", "day", "hours", "minutes"]}
                          value={formData.scheduled_time}
                          disabled={isReadOnly}
                          onChange={(newValue) =>
                            setFormData((prev) => ({ ...prev, scheduled_time: newValue }))
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  height: "45px",
                                  backgroundColor: "white",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#6d28d9",
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                </div>
              </>
            )}

            {/* Store Selection */}
            {pickerStores?.length > 1 && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">
                  {isEdit ? "Store" : "Store(s) *"}
                </p>
                <div className="mt-1">
                  {isEdit ? (
                    <Select
                      fullWidth
                      value={selectedStoreIds[0] || ""}
                      onChange={(e) => {
                        setSelectedStoreIds([e.target.value]);
                        setAssignedToIds([]);
                        setApproverIds([]);
                        // Equipment stays at its store. Goes through
                        // handleAssetChange so the data plate fields follow.
                        handleAssetChange(e.target.value === ticket?.store?.id ? ticket?.asset || null : null);
                        onStoreChange?.(e.target.value);
                      }}
                      disabled={isReadOnly}
                      size="small"
                      sx={selectSx}
                    >
                      {pickerStores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.store_name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Select
                      fullWidth
                      multiple
                      value={selectedStoreIds}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedStoreIds(val);
                        setAssignedToIds([]);
                        setApproverIds([]);
                        // Equipment stays at its store, and one asset can't
                        // cover a multi-store request.
                        if (!(val.length === 1 && val[0] === asset?.store?.id)) handleAssetChange(null);
                        // "Waiting: Approval" needs a single-store approver —
                        // drop it if the create just became multi-store.
                        if (val.length > 1 && formData.status === "waiting_approval") {
                          setFormData((prev) => ({ ...prev, status: "new" }));
                        }
                        if (val.length === 1) {
                          onStoreChange?.(val[0]);
                        }
                      }}
                      size="small"
                      renderValue={(selected) => {
                        if (selected.length === pickerStores.length) return "All Stores";
                        const names = pickerStores
                          .filter((s) => selected.includes(s.id))
                          .map((s) => s.store_name);
                        if (names.length <= 2) return names.join(", ");
                        return `${names[0]}, +${names.length - 1} more`;
                      }}
                      sx={selectSx}
                    >
                      {pickerStores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          <Checkbox
                            checked={selectedStoreIds.includes(store.id)}
                            sx={{ "&.Mui-checked": { color: "#6d28d9" } }}
                          />
                          <ListItemText primary={store.store_name} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </div>
              </div>
            )}

            {/* Assigned To (hidden when creating for multiple stores).
                Multi-select — a ticket can have several assignees. */}
            {canManageMaintenance && assigneeOptions.length > 0 && (isEdit || selectedStoreIds.length <= 1) && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">Assigned To</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    multiple
                    value={assignedToIds}
                    onChange={(e) => setAssignedToIds(e.target.value)}
                    disabled={isReadOnly}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected.length) {
                        return <span className="text-slate-400">Unassigned</span>;
                      }
                      const names = assigneeOptions
                        .filter((emp) => selected.includes(emp.id))
                        .map((emp) => emp.full_name);
                      if (names.length <= 2) return names.join(", ");
                      return `${names[0]}, +${names.length - 1} more`;
                    }}
                    sx={selectSx}
                  >
                    {assigneeOptions.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        <Checkbox
                          checked={assignedToIds.includes(emp.id)}
                          sx={{ "&.Mui-checked": { color: "#6d28d9" } }}
                        />
                        <ListItemText primary={emp.full_name} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {/* Issue Type */}
            <div className="mt-5">
              <p className="text-sm text-slate-500">Issue Type *</p>
              <div className="mt-1">
                <Select
                  fullWidth
                  value={formData.issue_type}
                  name="issue_type"
                  onChange={handleChange}
                  disabled={isReadOnly}
                  size="small"
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="" disabled>
                    Select issue type...
                  </MenuItem>
                  {issueTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {/* Asset (optional). Only once the store has assets, so companies
                that don't track equipment never see it. */}
            {canLinkAsset && (storeAssets.length > 0 || asset) && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">Asset</p>
                <div className="mt-1">
                  <AssetPicker
                    assets={storeAssets}
                    value={asset}
                    onChange={handleAssetChange}
                    disabled={isReadOnly}
                  />
                </div>
                {openAssetTickets.length > 0 && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Already open on this asset:{" "}
                    {openAssetTickets.slice(0, 2).map((t, i) => (
                      <span key={t.uuid}>
                        {i > 0 && ", "}
                        <span className="font-semibold">
                          {t.ticket_number != null ? `#${t.ticket_number} ` : ""}{t.title}
                        </span>
                      </span>
                    ))}
                    {openAssetTickets.length > 2 && ` and ${openAssetTickets.length - 2} more`}
                    . Consider adding a comment there instead.
                  </div>
                )}
              </div>
            )}

            {/* Equipment details (optional). Hidden while creating a recurring
                template: templates carry no equipment data and generated tickets
                would silently drop anything typed here. */}
            {showEquipmentBlock && !isRecurring && (
              <div className="mt-6 pt-5 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700">Equipment details</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {/* Only when a visible field actually holds the asset's value. */}
                  {Object.entries(equipmentFromAsset(asset)).some(
                    ([key, value]) => String(formData.equipment_details?.[key] || "").trim() === value
                  )
                    ? `Filled in from ${[asset.code, asset.name].filter(Boolean).join(" ")}. Change anything that's different on this unit.`
                    : "Optional. Check the data plate on the unit and add whatever you can read."}
                </p>
                {EQUIPMENT_FIELDS.map((f, i) => (
                  <div key={f.key} className={i === 0 ? "mt-4" : "mt-5"}>
                    <p className="text-sm text-slate-500">{f.label}</p>
                    <div className="mt-1">
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={formData.equipment_details?.[f.key] || ""}
                        onChange={(e) => setEquipmentField(f.key, e.target.value)}
                        disabled={isReadOnly}
                        inputProps={{ maxLength: 255, "aria-label": f.label }}
                        sx={textFieldSx}
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Other data plate info</p>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.equipment_details?.plate_notes || ""}
                      onChange={(e) => setEquipmentField("plate_notes", e.target.value)}
                      disabled={isReadOnly}
                      multiline
                      minRows={2}
                      inputProps={{ maxLength: 1000, "aria-label": "Other data plate info" }}
                      sx={textFieldSx}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cost and parts (optional), recorded on existing maintenance
                tickets so each asset's spend and repeat parts roll up. */}
            {isEdit && effectiveCategory === "maintenance" && (
              <CostAndPartsSection
                repairCost={repairCost}
                onRepairCostChange={setRepairCost}
                parts={partsUsed}
                onPartsChange={setPartsUsed}
                textFieldSx={textFieldSx}
                // Invoices often arrive after the work is marked done, so a
                // completed ticket keeps this section editable with its own
                // save (the rest of the form stays locked).
                onSave={isReadOnly ? () => handleSubmit() : undefined}
                isSaving={isSubmitting && submittingAction === "update"}
              />
            )}

            {/* Menu details (all optional), on every menu ticket. Hidden while
                creating a recurring template for the same reason as equipment.
                Same field pattern as the rest of the drawer. */}
            {isMenu && !isRecurring && (
              <div className="mt-6 pt-5 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700">Menu details</p>
                <p className="text-xs text-slate-400 mt-0.5">Optional. Fill in what applies to this change.</p>

                {/* Target go-live date */}
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">Target go-live date</p>
                    {formData.menu_details?.go_live_date && !isReadOnly && (
                      <button
                        type="button"
                        className="text-xs text-violet-700 hover:underline"
                        onClick={() => setMenuField("go_live_date", "")}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="mt-1">
                    <RightworkDatePicker
                      value={formData.menu_details?.go_live_date || null}
                      onChange={(v) => setMenuField("go_live_date", v)}
                      disabled={isReadOnly}
                      height={45}
                      fullWidth
                    />
                  </div>
                </div>

                {/* Which menus (multi-select chips, like the Notion form) */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500" id="menu-menus-label">
                    Which menus need to be updated{" "}
                    <span className="text-slate-400">(select as many as you like)</span>
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2" role="group" aria-labelledby="menu-menus-label">
                    {menuOptions.map((opt) => {
                      const selected = (formData.menu_details?.menus || []).includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={selected}
                          disabled={isReadOnly}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 ${
                            selected
                              ? "border-violet-600 bg-violet-50 text-violet-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => {
                            const current = formData.menu_details?.menus || [];
                            setMenuField(
                              "menus",
                              selected ? current.filter((v) => v !== opt.value) : [...current, opt.value]
                            );
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {formData.menu_details?.menus_other && (
                    <p className="text-xs text-slate-500 mt-2">
                      Other menus: {formData.menu_details.menus_other}
                    </p>
                  )}
                </div>

                {/* Online ordering (Yes / No; click the selected one again to clear) */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500" id="menu-online-label">
                    Will this item be available for online ordering?
                  </p>
                  <div className="mt-1 flex gap-2" role="group" aria-labelledby="menu-online-label">
                    {[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ].map((opt) => {
                      const selected = formData.menu_details?.online_ordering === opt.value;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          aria-pressed={selected}
                          disabled={isReadOnly}
                          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 ${
                            selected
                              ? "border-violet-600 bg-violet-50 text-violet-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => setMenuField("online_ordering", selected ? null : opt.value)}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Item this replaces */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">What item does this replace?</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="e.g. Classic Burger (leave blank if none)"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.menu_details?.replaces_item || ""}
                      onChange={(e) => setMenuField("replaces_item", e.target.value)}
                      disabled={isReadOnly}
                      inputProps={{ maxLength: 255 }}
                      sx={textFieldSx}
                    />
                  </div>
                </div>

                {/* Kill date for the replaced item */}
                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">Kill date for the replaced item</p>
                    {formData.menu_details?.kill_date && !isReadOnly && (
                      <button
                        type="button"
                        className="text-xs text-violet-700 hover:underline"
                        onClick={() => setMenuField("kill_date", "")}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="mt-1">
                    <RightworkDatePicker
                      value={formData.menu_details?.kill_date || null}
                      onChange={(v) => setMenuField("kill_date", v)}
                      disabled={isReadOnly}
                      height={45}
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Priority */}
            <div className="mt-5">
              <p className="text-sm text-slate-500">Priority</p>
              {!optionsReady && (
                <p className="text-xs text-amber-700 mt-1">
                  {optionsStatus === "error" ? (
                    <>
                      Couldn't load this company's ticket options.{" "}
                      <button type="button" className="underline font-medium" onClick={onRetryOptions}>
                        Retry
                      </button>
                    </>
                  ) : (
                    "Loading ticket options…"
                  )}
                </p>
              )}
              <div className="mt-1">
                <Select
                  fullWidth
                  value={formData.priority}
                  name="priority"
                  onChange={handleChange}
                  disabled={isReadOnly || !optionsReady}
                  size="small"
                  sx={selectSx}
                >
                  {priorityOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {/* File Upload (hidden when recurring) */}
            {!isRecurring && (
            <div className="mt-5">
              <p className="text-sm text-slate-500">Attachments</p>
              <div className="mt-1">
                {fileUploads.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {fileUploads.map((file) => {
                      const kind = getFileKind(file);
                      return (
                      <div
                        key={file.uuid}
                        className="relative group w-[120px] h-[120px] rounded-lg border border-slate-200 overflow-hidden bg-white cursor-pointer"
                        onClick={() => {
                          if (!file.presigned_url) return;
                          if (kind === "video") {
                            setSelectedVideo(file.presigned_url);
                            setShowVideoModal(true);
                          } else if (kind === "image") {
                            setSelectedImage(file.presigned_url);
                            setShowLightbox(true);
                          } else {
                            window.open(file.presigned_url, "_blank", "noopener,noreferrer");
                          }
                        }}
                      >
                        {!file.presigned_url ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-50">
                            <ImageIcon sx={{ color: "#94a3b8", fontSize: 32 }} />
                          </div>
                        ) : kind === "video" ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-900">
                            <PlayCircleOutlineIcon sx={{ color: "white", fontSize: 40 }} />
                          </div>
                        ) : kind === "image" ? (
                          <img
                            src={file.presigned_url}
                            alt="Upload"
                            className="w-full h-full object-cover"
                          />
                        ) : kind === "pdf" ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 p-2">
                            <PictureAsPdfIcon sx={{ color: "#dc2626", fontSize: 32 }} />
                            <span className="mt-1 text-[11px] leading-tight text-slate-600 text-center break-all line-clamp-2">
                              {file.name || "PDF"}
                            </span>
                          </div>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 p-2">
                            <InsertDriveFileIcon sx={{ color: "#94a3b8", fontSize: 32 }} />
                            <span className="mt-1 text-[11px] leading-tight text-slate-600 text-center break-all line-clamp-2">
                              {file.name || "File"}
                            </span>
                          </div>
                        )}
                        {!isReadOnly && (
                          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile(file.uuid);
                              }}
                              sx={{
                                backgroundColor: "rgba(0,0,0,0.5)",
                                color: "white",
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                                width: 24,
                                height: 24,
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
                {!isReadOnly && (
                  <label className="flex items-center gap-2 text-violet-700 cursor-pointer hover:text-violet-800">
                    {isFileLoading ? (
                      <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
                    ) : (
                      <AttachFileIcon fontSize="small" />
                    )}
                    <span className="text-sm">
                      {isFileLoading ? "Uploading..." : "Attach File"}
                    </span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.heic,.heif,.svg,.pdf,.mp4,.mov,.webm,.m4v,.avi,.hevc"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={isFileLoading}
                    />
                  </label>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Spacer for footer */}
          <div className="h-[200px]"></div>
          </> )}
        </div>

        {activeTab === "details" && (
        <DrawerFooterContainerLarge>
          <div className="w-full space-y-2">
            {["completed", "closed"].includes(ticket?.status) ? (
              <>
                {canManageMaintenance && (
                  <ButtonFilledCancel text="Archive Ticket" onClick={handleDelete} />
                )}
                <div
                  className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 border border-violet-600 hover:border-violet-700 text-white text-lg flex items-center justify-center cursor-pointer"
                  onClick={isSubmitting ? undefined : () => handleSubmit("in_progress")}
                >
                  {submittingAction === "in_progress" ? "Saving..." : "Reopen Ticket"}
                </div>
              </>
            ) : isEdit ? (
              <>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={isSubmitting ? undefined : () => handleSubmit()}
                      className="w-full h-[52px] rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-white text-violet-700 hover:text-violet-800 hover:border-violet-300 hover:shadow-sm transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <EditOutlinedIcon sx={{ fontSize: 20 }} />
                      <span className="text-lg font-medium">
                        {submittingAction === "update" ? "Saving..." : "Update Ticket"}
                      </span>
                    </button>
                  </div>
                  {canManageMaintenance && (
                    <div
                      className="w-[52px] h-[52px] rounded-xl border border-red-200 bg-red-50/70 text-red-500 flex items-center justify-center cursor-pointer hover:bg-red-100 hover:border-red-300 transition-colors"
                      onClick={handleDelete}
                    >
                      <DeleteIcon sx={{ color: "#ef4444" }} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={isSubmitting || approvalPending}
                  className={`w-full py-3 rounded-xl border text-lg font-medium flex items-center justify-center gap-2 transition-all disabled:cursor-not-allowed ${
                    approvalPending
                      ? "bg-slate-200 border-slate-200 text-slate-400"
                      : approvalRejected
                      ? "bg-slate-700 hover:bg-slate-800 border-slate-700 hover:border-slate-800 text-white disabled:opacity-70"
                      : "bg-emerald-600 hover:bg-emerald-700 border-emerald-600 hover:border-emerald-700 text-white disabled:opacity-70"
                  }`}
                  onClick={
                    isSubmitting || approvalPending
                      ? undefined
                      : () => handleSubmit(approvalRejected ? "closed" : "completed")
                  }
                >
                  {approvalRejected ? (
                    <CancelOutlinedIcon sx={{ fontSize: 22 }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />
                  )}
                  {submittingAction === "closed"
                    ? "Closing..."
                    : submittingAction === "completed"
                    ? "Saving..."
                    : approvalRejected
                    ? (isMenu ? "Cancel Request" : "Close Ticket")
                    : (isMenu ? "Mark Live" : "Complete Ticket")}
                </button>
                {approvalPending && (
                  <p className="text-xs text-center text-slate-400">
                    {isMenu
                      ? "This request must be approved before it can go live."
                      : "This ticket must be approved before it can be completed."}
                  </p>
                )}
              </>
            ) : (
              <>
                <ButtonFilled
                  text={isSubmitting ? "Saving..." : isRecurring ? "Create Recurring Ticket" : "Create Ticket"}
                  onClick={isSubmitting ? undefined : () => handleSubmit()}
                />
                <ButtonOutlined text="Cancel" onClick={onClose} />
              </>
            )}
          </div>
        </DrawerFooterContainerLarge>
        )}
        {activeTab === "conversation" && isEdit && (
          <div className="w-full absolute bottom-0 bg-white border-t border-slate-300 px-6 py-4 z-30">
            <div className="flex items-end gap-2">
              <TextField
                fullWidth
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handlePostComment();
                  }
                }}
                multiline
                maxRows={4}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
              <IconButton
                onClick={handlePostComment}
                disabled={!commentText.trim() || postingComment}
                sx={{
                  backgroundColor: commentText.trim() ? "#6d28d9" : "#e2e8f0",
                  color: "white",
                  width: 40,
                  height: 40,
                  "&:hover": { backgroundColor: commentText.trim() ? "#5b21b6" : "#e2e8f0" },
                  "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {postingComment ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  <SendIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default MaintenanceTicketDrawer;
